import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  ScriptableContext,
  ChartOptions,
  Plugin
} from 'chart.js';
import { format } from 'date-fns';
import {
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO,
  HistoricalBiomarkerDataElement
} from '../../../types/apiGeneratedTypes';
import { useEffect, useState, useMemo } from 'react';
import { getBiomarkersResultsByAppointmentHistoricalData } from '../../../api/biomarkers';
import Loader from '../../BaseComponents/Loader';

ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip
);

interface BiomarkerBinaryChartProps {
  biomarker: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerDTO;
  value: string;
  member_id: string;
  refreshKey?: number;
}

export const BiomarkerBinaryChart = ({
  biomarker,
  value,
  member_id,
  refreshKey = 0
}: BiomarkerBinaryChartProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<HistoricalBiomarkerDataElement[]>([]);

  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const resp = await getBiomarkersResultsByAppointmentHistoricalData(
          biomarker.id,
          member_id
        );
        setData(resp.historical_data);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    };
    fetchChartData();
  }, [biomarker, member_id, refreshKey]);

  const dataPoints = useMemo(() => {
    const points = data.map((hd) => ({
      x: format(hd.date, 'yyyy-MM-dd'),
      y: hd.value === 'POSITIVE' ? 7 : 3,
      value: hd.value
    }));
    if (points.length === 1) {
      points.push(points[0]);
    }
    return points;
  }, [data]);

  const chartData = useMemo(() => ({
    labels: dataPoints.map((pt) => format(new Date(pt.x), 'MMM yyyy')),
    datasets: [
      {
        label: 'Status',
        data: dataPoints.map((pt) => pt.y),
        borderColor: '#0C20121A',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0,
        pointBackgroundColor: (ctx: ScriptableContext<'line'>) => {
          const pointValue = dataPoints[ctx.dataIndex]?.value;
          if (!pointValue) return '#000';

          const isPositive = pointValue === 'POSITIVE';
          const isPositiveNormal = biomarker.value_meaning === 'POSITIVE_NORMAL';

          if (isPositiveNormal) {
            return isPositive ? '#b4d334' : '#d94939';
          }
          return isPositive ? '#d94939' : '#b4d334';
        },
        pointRadius: 6,
        pointHoverRadius: 6,
        borderWidth: 3
      },
      {
        label: 'Divider',
        data: dataPoints.map(() => 5),
        borderColor: '#0C20121A',
        borderDash: [5, 5],
        borderWidth: 1,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0
      }
    ]
  }), [dataPoints, biomarker.value_meaning]);

  const conditionalFillPlugin: Plugin<'line'> = useMemo(() => {
    const latestValue = value;
    const shouldFillPositive = latestValue === 'POSITIVE';
    const shouldFillNegative = latestValue === 'NEGATIVE';
    
    if (!shouldFillPositive && !shouldFillNegative) {
      return {
        id: 'conditionalFill',
        afterDatasetsDraw: () => { }
      };
    }

    const fillColor = biomarker.value_meaning === 'POSITIVE_NORMAL'
      ? latestValue === 'POSITIVE'
        ? '#b4d33426' 
        : '#d9493926'  
      : latestValue === 'POSITIVE'
        ? '#d9493926' 
        : '#b4d33426';

    return {
      id: 'conditionalFill',
      afterDatasetsDraw: (chart: any) => {
        const { ctx, chartArea, scales } = chart;
        const yAxis = scales.y;
        
        if (!chartArea) return;
        
        const dividerY = yAxis.getPixelForValue(5);
        
        ctx.save();
        ctx.fillStyle = fillColor;
        
        if (shouldFillPositive) {
          // Fill the entire Positive row (from top to divider)
          ctx.fillRect(
            chartArea.left,
            chartArea.top,
            chartArea.right - chartArea.left,
            dividerY - chartArea.top
          );
        } else if (shouldFillNegative) {
          // Fill the entire Negative row (from divider to bottom)
          ctx.fillRect(
            chartArea.left,
            dividerY,
            chartArea.right - chartArea.left,
            chartArea.bottom - dividerY
          );
        }
        
        ctx.restore();
      }
    };
  }, [value, biomarker.value_meaning]);

  const latestValueVerticalLinePlugin: Plugin<'line'> = useMemo(() => ({
    id: 'latestValueVerticalLine',
    afterDraw: (chart: any) => {
      const { ctx, chartArea, data } = chart;

      if (!chartArea || !data.datasets[0] || !data.datasets[0].data) return;

      const datasetMeta = chart.getDatasetMeta(0);
      const points = datasetMeta.data;

      if (points.length === 0) return;

      const latestPoint = points[points.length - 1] as { x: number };
      const latestX = latestPoint.x;

      ctx.save();
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);

      ctx.beginPath();
      ctx.moveTo(latestX, chartArea.top);
      ctx.lineTo(latestX, chartArea.bottom);
      ctx.stroke();

      ctx.restore();
    }
  }), []);

  const yAxisLabelBackgroundPlugin: Plugin<'line'> = useMemo(() => {
    const isPositive = value === 'POSITIVE';
    const isNegative = value === 'NEGATIVE';
    const isPositiveNormal = biomarker.value_meaning === 'POSITIVE_NORMAL';

    const positiveColor = isPositiveNormal ? '#C1D733' : '#F49690';
    const negativeColor = isPositiveNormal ? '#F49690' : '#C1D733';
    
    return {
      id: 'yAxisLabelBackground',
      afterDraw: (chart: any) => {
        const { ctx, scales, chartArea } = chart;
        const yAxis = scales.y;

        if (!yAxis || !chartArea) return;

        const tickConfigs = [
          { value: 3, label: 'Negative', color: negativeColor, shouldColor: isNegative },
          { value: 7, label: 'Positive', color: positiveColor, shouldColor: isPositive }
        ];

        const axisOptions = yAxis.options as { ticks?: { font?: { size?: number; family?: string; weight?: string }; padding?: number } };
        const fontSize = axisOptions?.ticks?.font?.size || 12;
        const fontFamily = axisOptions?.ticks?.font?.family || 'sans-serif';
        const fontWeight = axisOptions?.ticks?.font?.weight || 'bold';
        const tickPadding = axisOptions?.ticks?.padding || 10;
        
        tickConfigs.forEach(({ value: tickValue, label, color: backgroundColor, shouldColor }) => {
          const yPos = yAxis.getPixelForValue(tickValue);
          
          ctx.save();
          ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
          
          const textWidth = ctx.measureText(label).width;
          const textHeight = fontSize;
          const bgPadding = 6;
          const labelX = chartArea.left - tickPadding - 5;
          
          const rectX = labelX - textWidth - bgPadding;
          const rectY = yPos - textHeight / 2 - bgPadding / 2;
          const rectWidth = textWidth + bgPadding * 2;
          const rectHeight = textHeight + bgPadding;
          const borderRadius = 4;
          
          // Draw white background to cover default label
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(rectX - 2, rectY - 2, rectWidth + 4, rectHeight + 4);
          
          // Only color the label if it matches the latest value
          if (shouldColor && backgroundColor) {
            ctx.fillStyle = backgroundColor + 'CC';
            ctx.beginPath();
            ctx.moveTo(rectX + borderRadius, rectY);
            ctx.lineTo(rectX + rectWidth - borderRadius, rectY);
            ctx.quadraticCurveTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + borderRadius);
            ctx.lineTo(rectX + rectWidth, rectY + rectHeight - borderRadius);
            ctx.quadraticCurveTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - borderRadius, rectY + rectHeight);
            ctx.lineTo(rectX + borderRadius, rectY + rectHeight);
            ctx.quadraticCurveTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - borderRadius);
            ctx.lineTo(rectX, rectY + borderRadius);
            ctx.quadraticCurveTo(rectX, rectY, rectX + borderRadius, rectY);
            ctx.closePath();
            ctx.fill();
            
            ctx.fillStyle = '#FFFFFF';
          } else {
            ctx.fillStyle = '#000000';
          }
          
          ctx.font = `bold ${fontSize}px ${fontFamily}`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'middle';
          ctx.fillText(label, labelX, yPos);
          
          ctx.restore();
        });
      }
    };
  }, [value, biomarker.value_meaning]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        min: 1,
        max: 9,
        ticks: {
          callback: (val: string | number) => {
            const num = Number(val);
            if (num === 3) return 'Negative';
            if (num === 7) return 'Positive';
            return '';
          },
          stepSize: 1,
          font: {
            weight: 'bold' as const,
            size: 12
          },
          padding: 10
        },
        grid: {
          display: false,
          drawTicks: false
        },
        border: {
          display: true,
          color: '#999',
          width: 1
        }
      },
        x: {
        type: 'category',
        afterBuildTicks: (axis: any) => {
          const originalTicks = [...axis.ticks];
          const seen = new Set<string>();
          const filteredTicks: typeof originalTicks = [];

          // Keep all unique month/year labels in order
          originalTicks.forEach((tick) => {
            const label = tick.label as string;
            if (!label) return;

            if (!seen.has(label)) {
              seen.add(label);
              filteredTicks.push(tick);
            }
          });

          axis.ticks = filteredTicks.length > 0 ? filteredTicks : originalTicks;
        },
        ticks: {
          display: true,
          color: '#999',
          font: {
            size: 12
          },
          padding: 10,
          autoSkip: false,
          maxTicksLimit: 20,
          maxRotation: 0,
          minRotation: 0,
          callback: function(value: string | number) {
            try {
              const index = typeof value === 'number' ? value : parseInt(String(value), 10);
              if (isNaN(index)) return '';
              const label = this.getLabelForValue(index) as string;
              return label || '';
            } catch {
              return '';
            }
          }
        },
        grid: {
          display: false
        },
        border: {
          display: true,
          color: '#999',
          width: 1
        }
      }
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        filter: (tooltipItem) => tooltipItem.datasetIndex === 0,
        backgroundColor: '#000000',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: '#000000',
        borderWidth: 1,
        padding: 12,
        callbacks: {
          label: (context: { dataIndex: number }) => {
            const pointData = dataPoints[context.dataIndex];
            return pointData?.value === 'POSITIVE' ? 'Positive' : 'Negative';
          }
        }
      },
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x',
      intersect: true
    }
  };

  if (loading) return <Loader />;
  if (error) return <div>Error loading chart data</div>;
  if (!data.length) return <div>No data</div>;

  return (
    <div style={{ width: '100%' }}>
      <Line
        data={chartData}
        options={options}
        plugins={[conditionalFillPlugin, latestValueVerticalLinePlugin, yAxisLabelBackgroundPlugin]}
      />
    </div>
  );
};

export default BiomarkerBinaryChart;
